import { toastr } from 'react-redux-toastr';
import i18n from 'i18next';

import { axiosInstance, ENDPOINT } from 'common/constants';
import { handleResponse } from 'common/helpers';

import { firmwareDeploymentConstants } from '../constants/firmwareDeployment.constants';

export const getFirmwareDeployments = (filters, page, all = false) => (dispatch, getState) => {
  const request = page => ({ type: firmwareDeploymentConstants.GET_FIRMWARE_DEPLOYMENT_REQUEST, page });
  const success = (items, page, total_pages, total_count, all) => (
    { type: firmwareDeploymentConstants.GET_FIRMWARE_DEPLOYMENT_SUCCESS, items, page, total_pages, total_count, all }
  );
  const failure = error => ({ type: firmwareDeploymentConstants.GET_FIRMWARE_DEPLOYMENT_FAILURE, error });
  const firmwareDeploymentsState = getState().firmwareDeployments;
  const params = {
    page: page || firmwareDeploymentsState.deployments.page,
    filters: filters || firmwareDeploymentsState.filters.filters,
    per_page: firmwareDeploymentConstants.NUMBER_OF_FIRMWARE_DEPLOYMENTS_PER_PAGE,
    all
  };
  dispatch(request(params.page));

  return handleResponse(axiosInstance.get(ENDPOINT.GET_FIRMWARE_DEPLOYMENTS, { params })).then(
    (res) => {
      dispatch(success(res.items, res.page, res.total_pages, res.items.length, all));
      return res;
    },
    (err) => {
      dispatch(failure(err));
      toastr.error(err.message);
    }
  );
};

export const loadMoreFirmwareDeployments = () => (dispatch, getState) => {
  const deployments = getState().firmwareDeployments.deployments;
  const page = deployments.page;
  if (deployments.listLoading) return;
  dispatch({ type: firmwareDeploymentConstants.INCREASE_FIRMWARE_DEPLOYMENT_PAGE, page: page + 1 });
  dispatch(getFirmwareDeployments(null, page + 1));
};

export const setFirmwareDeploymentFilters = filters => (dispatch) => {
  dispatch({ type: firmwareDeploymentConstants.SET_FIRMWARE_DEPLOYMENT_FILTERS, filters });
  dispatch(getFirmwareDeployments(filters, 1, true));
};

export const getFirmwares = params => (dispatch) => {
  const request = () => ({ type: firmwareDeploymentConstants.GET_FIRMWARES_REQUEST });
  const success = items => ({ type: firmwareDeploymentConstants.GET_FIRMWARES_SUCCESS, items });
  const failure = error => ({ type: firmwareDeploymentConstants.GET_FIRMWARES_FAILURE, error });

  dispatch(request());
  return handleResponse(axiosInstance.get(ENDPOINT.GET_FIRMWARES, { params })).then(
    (res) => {
      dispatch(success(res.items));
      return res;
    },
    (err) => {
      dispatch(failure(err));
      toastr.error(err.message);
    });
};

export const addFirmwareDeployment = params => dispatch => handleResponse(
  axiosInstance.post(ENDPOINT.CREATE_FIRMWARE_DEPLOYMENT, params)).then(
  (res) => {
    toastr.success(i18n.t('firmwareDeployment.toastr.createdSuccess', { defaultValue: 'Firmware deployment created successfully' }));
    dispatch(getFirmwareDeployments(null, 1, true));
    return res;
  },
  (err) => {
    toastr.error(err.message);
  }
);

export const verifyDeviceDeployment = params => () => handleResponse(
  axiosInstance.get(ENDPOINT.VERIFY_DEVICE_DEPLOYMENTS, { params })).then(
  res => res,
  (err) => {
    toastr.error(err.message);
    throw err;
  }
);

export const updateFirmwareDeployment = (deployment_id, params) => dispatch => handleResponse(
  axiosInstance.put(ENDPOINT.UPDATE_FIRMWARE_DEPLOYMENT(deployment_id), params)).then(
  (res) => {
    toastr.success(i18n.t('firmwareDeployment.toastr.updatedSuccess', { defaultValue: 'Firmware deployment updated successfully' }));
    dispatch(getFirmwareDeployments(null, 1, true));
    return res;
  },
  (err) => {
    toastr.error(err.message);
  }
);

// Get TX serial numbers and IDs
export const getAllOpsTx = () => (dispatch) => {
  const request = () => ({ type: firmwareDeploymentConstants.GET_ALL_TX_SERIALS_REQUEST });
  const success = items => ({ type: firmwareDeploymentConstants.GET_ALL_TX_SERIALS_SUCCESS, items });
  const failure = error => ({ type: firmwareDeploymentConstants.GET_ALL_TX_SERIALS_FAILURE, error });

  dispatch(request());
  return handleResponse(axiosInstance.get(ENDPOINT.GET_TX_SERIAL_NUMBERS, { params: { all: 1 } })).then(
    (res) => {
      dispatch(success(res.devices));
      return res.devices;
    },
    (err) => {
      dispatch(failure(err));
      return [];
    });
};

// Get Mote serial numbers and IDs
export const getAllOpsMotes = () => (dispatch) => {
  const request = () => ({ type: firmwareDeploymentConstants.GET_ALL_MOTE_SERIALS_REQUEST });
  const success = items => ({ type: firmwareDeploymentConstants.GET_ALL_MOTE_SERIALS_SUCCESS, items });
  const failure = error => ({ type: firmwareDeploymentConstants.GET_ALL_MOTE_SERIALS_FAILURE, error });

  dispatch(request());
  return handleResponse(axiosInstance.get(ENDPOINT.GET_MOTE_SERIAL_NUMBERS, { params: { all: 1 } })).then(
    (res) => {
      dispatch(success(res.devices));
      return res.devices;
    },
    (err) => {
      dispatch(failure(err));
      return [];
    });
};

export const deleteFirmwareDeployment = deployment_id => dispatch => handleResponse(
  axiosInstance.delete(ENDPOINT.DELETE_FIRMWARE_DEPLOYMENT(deployment_id))).then(
  (res) => {
    toastr.success(i18n.t('firmwareDeployment.toastr.deletedSuccess', { defaultValue: 'Firmware deployment deleted successfully' }));
    dispatch(getFirmwareDeployments(null, 1, true));
    return res;
  },
  (err) => {
    toastr.error(err.message);
  }
);

export const getFirmwareDeploymentDetails = deployment_id => (dispatch) => {
  const request = () => ({ type: firmwareDeploymentConstants.GET_FIRMWARE_DEPLOYMENT_DETAILS_REQUEST });
  const success = details => ({ type: firmwareDeploymentConstants.GET_FIRMWARE_DEPLOYMENT_DETAILS_SUCCESS, details });
  const failure = error => ({ type: firmwareDeploymentConstants.GET_FIRMWARE_DEPLOYMENT_DETAILS_FAILURE, error });

  dispatch(request());
  return handleResponse(axiosInstance.get(ENDPOINT.GET_FIRMWARE_DEPLOYMENT_DETAILS(deployment_id))).then(
    (res) => {
      dispatch(success(res));
    },
    (err) => {
      dispatch(failure(err.message));
      toastr.error(err.message);
    });
};

export const getDeploymentDevices = (deployment_id, search_key = '') => (dispatch) => {
  const request = () => ({ type: firmwareDeploymentConstants.GET_FIRMWARE_DEPLOYMENT_AFFECTED_DEVICES_REQUEST });
  const success = devices => ({ type: firmwareDeploymentConstants.GET_FIRMWARE_DEPLOYMENT_AFFECTED_DEVICES_SUCCESS, devices });
  const failure = error => ({ type: firmwareDeploymentConstants.GET_FIRMWARE_DEPLOYMENT_DETAILS_FAILURE, error });

  const params = {
    search_key
  };
  dispatch(request());
  return handleResponse(axiosInstance.get(ENDPOINT.GET_FIRMWARE_DEPLOYMENT_DEVICES(deployment_id), { params })).then(
    (res) => {
      dispatch(success(res.affected_devices));
    },
    (err) => {
      dispatch(failure(err.message));
      toastr.error(err.message);
    });
};

export const retryDeployment = (deployment_id, device_ids) => (dispatch) => {
  const request = () => ({ type: firmwareDeploymentConstants.RETRY_FIRMWARE_DEPLOYMENT_DEVICES_REQUEST });
  const success = () => ({ type: firmwareDeploymentConstants.RETRY_FIRMWARE_DEPLOYMENT_DEVICES_SUCCESS });
  const failure = error => ({ type: firmwareDeploymentConstants.RETRY_FIRMWARE_DEPLOYMENT_DEVICES_FAILURE, error });

  dispatch(request());
  return handleResponse(
    axiosInstance.post(ENDPOINT.RETRY_FIRMWARE_DEPLOYMENT(deployment_id), { device_ids })).then(
    (res) => {
      dispatch(success());
      toastr.success(res.message);
      return res;
    },
    (err) => {
      dispatch(failure(err.message));
      toastr.error(err.message);
    }
  );
};
